/**
 * @fileoverview Hook de UI Layer para gestión de estados y renderizado de ClosedChannelMC
 * @module useClosedChannelUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 2.1.0
 */

import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../../../components/ButtonMC';
import PLATFORM_CONFIG from '../../../utils/platformConfig';

/**
 * Hook de UI Layer para gestión de estados y renderizado de canales cerrados del inbox.
 * 
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades,
 * enfocándose en la renderización y manejo de estados de interfaz para canales cerrados.
 */
export default function useClosedChannelUI({ conversation, type, slug, onClick }) {
  const { t } = useTranslation('InboxMC');
  
  /**
   * Handler para redireccionar a la página de recarga de saldo.
   * 
   * Maneja la navegación del usuario hacia la página de pricing para
   * que pueda recargar el saldo de su cuenta.
   */
  const onClickRechargeBalance = useCallback(() => {
    document.location.href = `/retailers/${slug}/pricing`;
  }, [slug]);

  /**
   * Handler para abrir tutorial de recarga de saldo en nueva ventana.
   * 
   * Abre el artículo de soporte sobre cómo agregar saldo a la cuenta
   * en una nueva pestaña del navegador.
   */
  const onClickShowTutorial = useCallback(() => {
    window.open(
      'https://support.mercately.com/es/articles/9742331-como-agregar-saldo-a-mi-cuenta',
      '_blank'
    );
  }, []);

  /**
   * Función memoizada que renderiza botones según el tipo de canal cerrado.
   * 
   * Proporciona renderizado condicional de botones basado en el contexto
   * del canal cerrado, optimizada para evitar re-renders innecesarios.
   */
  const renderButtons = useMemo(() => {
    switch (type) {
      case 'whatsapp':
        return () => (
          <>
            <ButtonComponent
              outlined
              onClick={() => onClick({ hasCustomer: true }, 'NewChat')}
              text={t('chatWindow.closedChannel.whatsappText')}
              tooltip={t('chatWindow.closedChannel.whatsappText')}
              icon={PLATFORM_CONFIG.whatsapp.icon}
              className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-px-8"
            />
            <ButtonComponent
              outlined
              onClick={() => onClick({ hasCustomer: true, type: 'reminder' }, 'NewChat')}
              text={t('chatWindow.closedChannel.reminderText')}
              tooltip={t('chatWindow.closedChannel.reminderText')}
              icon="flash-outline"
              className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-px-8"
            />
          </>
        );
      case 'balance':
        return () => (
          <>
            <ButtonComponent
              outlined
              onClick={onClickRechargeBalance}
              text={t('chatWindow.closedChannel.rechargeBalance')}
              tooltip={t('chatWindow.closedChannel.rechargeBalance')}
              className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-px-8"
            />
            <ButtonComponent
              outlined
              onClick={onClickShowTutorial}
              text={t('chatWindow.closedChannel.viewTutorial')}
              tooltip={t('chatWindow.closedChannel.viewTutorial')}
              className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-px-8"
            />
          </>
        );
      default:
        return () => (
          <ButtonComponent
            outlined
            onClick={() => onClick({ externalCreate: true, deal: { customer: conversation } }, 'ManageDeal')}
            text={t('chatWindow.closedChannel.createDeal')}
            tooltip={t('chatWindow.closedChannel.createDeal')}
            icon="briefcase-outline"
            className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-px-8"
          />
        );
    }
  }, [type, slug, onClick, onClickRechargeBalance, onClickShowTutorial, t]);

  /**
   * Objeto de retorno memoizado con todas las funciones de renderizado de UI.
   * 
   * Proporciona la interfaz pública del hook con funciones optimizadas
   * para renderizado de componentes de canal cerrado.
   */
  return {
    renderButtons,
  };
}
